import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import storage from 'util/storage'

// global
import { Icon, Flex } from 'shared/ui'
import { runOnNextTick } from 'util/functions'
import { isCmdEnter } from 'util/keys'

// re-used
import NavBar from 'components/App/DesktopView/shared/Composer/Email/Header/NavBar'
import EmojiPicker from 'components/App/DesktopView/shared/EmojiPicker'
import ToggleKbSearch from 'ducks/ticketing/components/ToggleKbSearch'
import AssignmentBadge from 'components/App/DesktopView/shared/AssignmentDropdown/Badge'
import ReplyStateIndicator from 'components/ReplyStateIndicator'
import Attachments from 'components/Attachments'
import ExpandedHeader from 'components/App/DesktopView/shared/Composer/Header/Expanded'
import FormSwitcherButton from 'components/App/DesktopView/ReplyForm/shared/FormSwitcherButton'
import EditorDropzoneIndicator from 'components/App/DesktopView/shared/Composer/shared/EditorDropzoneIndicator'
import withDropzone from 'components/Dropzone'

// styles
import styles from 'components/App/DesktopView/CommunicationForms/Reply/shared/styles.less'

// shared
import Button from 'components/App/DesktopView/CommunicationForms/Reply/shared/SendButton'
import AssignmentDropdown from 'components/App/DesktopView/CommunicationForms/Reply/shared/AssignmentDropdown'
import DeleteDraftButton from 'components/App/DesktopView/CommunicationForms/Reply/shared/DeleteDraftButton'
import AttachmentButton from 'components/App/DesktopView/CommunicationForms/Reply/shared/AttachmentButton'
import { handleDrop } from 'components/App/DesktopView/CommunicationForms/shared/dragAndDrop'

// local
import Editor from './EditorView'

const FlexWithDropzone = withDropzone(Flex, {
  indicatorComponent: EditorDropzoneIndicator,
  className: cn(styles.EditorDropzone, 'Dropzone'),
})

const FooterWithDropzone = withDropzone('div', {
  skipIndicator: true,
})

class ReplyEmailForm extends React.PureComponent {
  static propTypes = {
    draftId: PropTypes.string.isRequired,
  }

  state = {
    ccVisible: false,
    bccVisible: false,
    fromVisible: false,
    subjectVisible: false,
    formattingVisible: !!storage.get('showFormattingToolbar'),
    expandedVisible: this.props.initialExpandedVisible || false,
  }

  componentDidMount() {
    this.ensureDefaults()
    this.fixForwardingIfRequired()
  }

  // We need to do this, because we cannot hide the editor until changesets are fully loaded, so it is possible to start a draft _without_ defaults. Which is why we need to detect the moment the defaults are available and use doEnsureDefaults to safely set them.
  componentDidUpdate() {
    this.ensureDefaults()
    this.fixForwardingIfRequired()
  }

  onEditorContentChange = text => {
    const { debouncedHandleDraftChange, draftId, ticketId } = this.props

    if (!draftId && !text) return

    debouncedHandleDraftChange(ticketId, 'body', text, {
      draftId,
    })
    this.ensureDefaults()
  }

  onAgentChange = (_, groupId, agentId) => {
    const { handleDraftChange, draftId, ticketId } = this.props
    handleDraftChange(ticketId, 'assigneeId', agentId, {
      draftId,
    })
  }

  onGroupChange = (_, groupId) => {
    const { handleDraftChange, draftId, ticketId } = this.props
    handleDraftChange(ticketId, 'assigneeGroupId', groupId, {
      draftId,
    })
  }

  onActionClick = showField => {
    if (showField === 'cc') this.setState({ ccVisible: true })
    if (showField === 'bcc') this.setState({ bccVisible: true })
    if (showField === 'from') this.setState({ fromVisible: true })
    if (showField === 'subject') this.setState({ subjectVisible: true })
  }

  onSubjectChange = value => {
    const { debouncedHandleDraftChange, draftId, ticketId } = this.props

    debouncedHandleDraftChange(ticketId, 'title', value, {
      draftId,
    })
  }

  onMailboxClick = mailboxId => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'mailboxId', mailboxId, {
      draftId,
    })
  }

  onToUpdate = recipients => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'to', recipients, {
      draftId,
    })
  }

  onCcUpdate = recipients => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'cc', recipients, {
      draftId,
    })
  }

  onBccUpdate = recipients => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'bcc', recipients, {
      draftId,
    })
  }

  onSendAndOpen = () => {
    const {
      handleDraftMultiChange,
      sendDraft,
      draftId,
      ticketId,
      forwarding,
    } = this.props

    handleDraftMultiChange(
      ticketId,
      { state: 'opened', isForwarding: forwarding },
      { draftId }
    )
    sendDraft(draftId)
  }

  onSendAndClose = () => {
    const {
      handleDraftMultiChange,
      sendDraft,
      draftId,
      ticketId,
      forwarding,
    } = this.props

    handleDraftMultiChange(
      ticketId,
      { state: 'closed', isForwarding: forwarding },
      { draftId }
    )
    sendDraft(draftId)
  }

  onSendAndSnooze = snoozeUntil => {
    const {
      handleDraftMultiChange,
      sendDraft,
      draftId,
      ticketId,
      forwarding,
    } = this.props

    handleDraftMultiChange(
      ticketId,
      { snoozeUntil, isForwarding: forwarding },
      { draftId }
    )
    sendDraft(draftId)
  }

  onDeleteClick = () => {
    const { deleteDraft, ticketId, draftId } = this.props
    deleteDraft(ticketId, draftId)
  }

  onExpandedHeaderOpen = e => {
    e.stopPropagation()
    e.preventDefault()
    this.setState({ expandedVisible: true })
  }

  onExpandedHeaderClose = e => {
    e.stopPropagation()
    e.preventDefault()
    this.setState({ expandedVisible: false })
  }

  onReplyClick = () => {
    const {
      draftId,
      ticketId,
      initialExpandedVisible,
      handleChangeToReply,
    } = this.props
    handleChangeToReply(ticketId, draftId)

    if (!initialExpandedVisible) {
      this.setState({ expandedVisible: false })
    }
  }

  onForwardClick = () => {
    const { draftId, ticketId, handleChangeToForward } = this.props
    handleChangeToForward(ticketId, draftId)
    this.setState({ expandedVisible: true })
  }

  onAttachmentClick = () => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'touched', true, {
      draftId,
    })
  }

  onReplyAllClick = () => {
    const { doSwitchToReplyAll, ticketId, draftId } = this.props
    doSwitchToReplyAll(ticketId, draftId)
  }

  onDirectMessageClick = () => {
    const { doSwitchToDirectReply, ticketId, draftId } = this.props
    doSwitchToDirectReply(ticketId, draftId)
    this.setState({ ccVisible: false, bccVisible: false })
  }

  onUploadComplete = () => {
    const { handleDraftChange, ticketId, draftId } = this.props

    handleDraftChange(ticketId, 'touched', true, {
      draftId,
    })
  }

  ensureDefaults = () => {
    const {
      defaultsAvailable,
      draftId,
      defaultsSet,
      doEnsureDefaults,
    } = this.props
    if (draftId && !defaultsSet && defaultsAvailable) {
      runOnNextTick(() => doEnsureDefaults(draftId))
    }
  }

  // this is a way of detecting that we have a conflict between the query string
  // and the draft state. We have an option to drive the forwarding state from the url
  // but in order to be consistent, we need to "copy" that state from the url into the
  fixForwardingIfRequired = () => {
    const { isForwardingFromDraft, isForwardingFromQuery } = this.props
    if (isForwardingFromDraft === undefined && isForwardingFromQuery) {
      this.onForwardClick()
    }
  }

  afterAttachmentDelete = () => {
    const { handleDraftChange, draftId, ticketId } = this.props

    handleDraftChange(ticketId, 'touched', true, {
      draftId,
    })
  }

  toggleFormatting = () => {
    const { formattingVisible } = this.state
    this.setState({
      formattingVisible: !formattingVisible,
    })
    storage.set('showFormattingToolbar', !formattingVisible)
  }

  handleDropOnEditor = accepted => {
    const { uploadFiles, draftId } = this.props
    if (!draftId) this.onAttachmentClick()
    setTimeout(() => {
      const updatedDraftId = this.props.draftId
      handleDrop(accepted, updatedDraftId, uploadFiles)
    }, 100)
  }

  handleDropOnFooter = accepted => {
    const { uploadFiles, draftId } = this.props
    if (!draftId) this.onAttachmentClick()
    setTimeout(() => {
      const updatedDraftId = this.props.draftId
      handleDrop(accepted, updatedDraftId, uploadFiles)
    }, 100)
  }

  uploadAttachmentsFromEditorPaste = accepted => {
    const { uploadFiles, draftId } = this.props
    if (!draftId) this.onAttachmentClick()

    setTimeout(() => {
      const updatedDraftId = this.props.draftId
      uploadFiles(accepted, false, updatedDraftId)
    }, 100)
  }

  startTyping = () => {
    const {
      ticketId,
      doRealtimeAgentStartTicketTypingNotification,
    } = this.props
    doRealtimeAgentStartTicketTypingNotification(ticketId)
  }

  handleOnKeyDownEvent = e => {
    if (isCmdEnter(e)) {
      const { isSendable } = this.props
      e.preventDefault()
      e.stopPropagation()
      if (isSendable) {
        return this.onSendAndClose()
      }
      return true
    }
    this.startTyping()
    return true
  }

  render() {
    const {
      ccVisible,
      bccVisible,
      fromVisible,
      subjectVisible,
      formattingVisible,
      expandedVisible,
    } = this.state
    const {
      ticketId,
      draftId,
      mailboxId,
      subject,
      to,
      cc,
      bcc,
      assignmentLabel,
      assigneeGroupId,
      isSendable,
      forwarding,
      isOpen,
      replyType,
      onEditorFormFocus,
      onEditorFormBlur,
    } = this.props
    return (
      <div className={styles.form}>
        <div
          className={cn('inner', {
            forwarding,
          })}
        >
          <div className="header">
            <div className="top-section">
              <NavBar
                replying
                ticketId={ticketId}
                onExpandClick={this.onExpandedHeaderOpen}
                onReplyClick={this.onReplyClick}
                onForwardClick={this.onForwardClick}
                forwarding={forwarding}
              />
            </div>
            {expandedVisible && (
              <div className="bottom-section sui sui-transparent">
                <div className="sui-inner sui-inner-transparent">
                  <ExpandedHeader
                    className="expanded-header"
                    recipientsTo={to}
                    recipientsCc={cc}
                    recipientsBcc={bcc}
                    ccVisible={ccVisible || (cc && cc.length > 0)}
                    bccVisible={bccVisible || (bcc && bcc.length > 0)}
                    fromVisible={fromVisible}
                    subjectVisible={forwarding || subjectVisible}
                    subject={subject}
                    onClick={this.onClick}
                    onActionClick={this.onActionClick}
                    onSubjectChange={this.onSubjectChange}
                    mailboxId={mailboxId}
                    ticketId={ticketId}
                    onMailboxClick={this.onMailboxClick}
                    recipientsOpenUpward
                    onToUpdate={this.onToUpdate}
                    onCcUpdate={this.onCcUpdate}
                    onBccUpdate={this.onBccUpdate}
                    closeTo={this.onExpandedHeaderClose}
                    forwarding={forwarding}
                  >
                    {!forwarding && (
                      <FormSwitcherButton
                        key={ticketId}
                        ticketId={ticketId}
                        onReplyAllClick={this.onReplyAllClick}
                        onDirectMessageClick={this.onDirectMessageClick}
                        replyType={replyType}
                      />
                    )}
                  </ExpandedHeader>
                </div>
              </div>
            )}
          </div>
          <FlexWithDropzone
            className={cn('editorAndAttachments grui flex-col flex-2-1-auto', {
              'formatting-visible': formattingVisible,
            })}
            onDropCustom={this.handleDropOnEditor}
          >
            <Editor
              className="toolbar-bottom"
              ticketId={ticketId}
              draftId={draftId}
              onChange={this.onEditorContentChange}
              onKeyDown={this.handleOnKeyDownEvent}
              onFocus={onEditorFormFocus}
              onBlur={onEditorFormBlur}
              uploadAttachmentsFromEditorPaste={
                this.uploadAttachmentsFromEditorPaste
              }
            />

            <div id="email-editor-toolbar" />
            <div className="attachments">
              <Attachments
                forDraft
                draftId={draftId}
                ticketId={ticketId}
                afterDelete={this.afterAttachmentDelete}
              />
            </div>
          </FlexWithDropzone>
          <div className="divider" />
          <FooterWithDropzone
            className="footer"
            onDropCustom={this.handleDropOnFooter}
          >
            <div className="left-side actions">
              <Icon
                name={'text-format'}
                className={cn('formatting', {
                  visible: formattingVisible,
                })}
                onClick={this.toggleFormatting}
              />
              <div className="attachments">
                <AttachmentButton
                  onClick={this.onAttachmentClick}
                  draftId={draftId}
                  onUploadComplete={this.onUploadComplete}
                />
              </div>
              <DeleteDraftButton
                draftId={draftId}
                onClick={this.onDeleteClick}
              />
              <EmojiPicker />
              <ToggleKbSearch className="kb-search" />
            </div>
            <div className="right-side">
              <AssignmentDropdown
                direction="left"
                drilledDownGroupId={assigneeGroupId}
                key="assignment"
                onGroupSelect={this.onGroupChange}
                onAgentSelect={this.onAgentChange}
                trigger={
                  <div>
                    <AssignmentBadge
                      assignmentLabel={assignmentLabel}
                      size="small"
                      iconSize={24}
                      tooltipPosition="top"
                    />
                  </div>
                }
                upward
              />

              <div className="sendButtonContainer">
                <Button
                  actionLabel={forwarding ? 'Forward' : 'Reply'}
                  disabled={!isSendable}
                  isOpen={isOpen}
                  onSendOpenClick={this.onSendAndOpen}
                  onSendClosedClick={this.onSendAndClose}
                  onSnoozeOptionClick={this.onSendAndSnooze}
                />
                <ReplyStateIndicator
                  className={'collisionIndicator'}
                  hideDraftIndicator
                  conversationId={ticketId}
                />
              </div>
            </div>
          </FooterWithDropzone>
        </div>
      </div>
    )
  }
}

export default ReplyEmailForm
